import React, { useEffect, useState } from "react";
import { CombinedImage, CombinedMedia, ImageTagCardProps } from "./types";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import { Card, CardContent, CardTitle } from "../../misc/Card";
import { IoMdPricetag } from "react-icons/io";
import { Button } from "../../misc/Button";
import { FaEdit } from "react-icons/fa";
import InputField from "../../misc/InputField";
import { Badge } from "../../calendar/Badge";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { Switch } from "../../misc/Switch";
import { Star, Loader2 } from "lucide-react";
import ChangePrimaryImageDialog from "./ChangePrimaryImageDialog";
import { Images } from "../../../types/listingImages";
import { getApiErrorMessage } from "../../../utils/apiError";
import useAppSelector from "../../../hooks/useAppSelector";

const ImageTagCard: React.FC<ImageTagCardProps> = ({
    combinedMedia,
    carouselApi,
    propertyData,
    images,
    setImages,
    setPropertyData,
}) => {
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [tagEditing, setTagEditing] = useState(false);
    const [tempTag, setTempTag] = useState<string>("");
    const [savingTag, setSavingTag] = useState(false);
    const [savingPrimaryOrExclude, setSavingPrimaryOrExclude] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerMode, setPickerMode] = useState<"change" | "exclude">("change");
    const [pickerLockedId, setPickerLockedId] = useState<string | undefined>(
        undefined
    );

    const isMobile = useIsMobile(1200);
    // Admin-only audit card. The API also gates the audit fields server-side, so
    // non-admins never receive the data even if this flag were forced. MB-1314.
    const isAdmin = useAppSelector((state) => state.auth.isAdmin);

    const isSyncing = propertyData?.isSyncing || false;

    // Listen for carousel changes
    useEffect(() => {
        if (carouselApi) {
            const onSelect = () => {
                const newIndex = carouselApi.selectedScrollSnap();
                setCurrentMediaIndex(newIndex);
            };

            carouselApi.on("select", onSelect);

            // Initialize
            const initialIndex = carouselApi.selectedScrollSnap();
            setCurrentMediaIndex(initialIndex);

            return () => {
                carouselApi.off("select", onSelect);
            };
        }
    }, [carouselApi]);

    // Reset editing state and tempTag when current media changes
    const currentMedia = combinedMedia[currentMediaIndex];
    useEffect(() => {
        setTagEditing(false);
        if (currentMedia && isCombinedImage(currentMedia)) {
            setTempTag(currentMedia.tag || "");
        } else {
            setTempTag("");
        }
    }, [currentMedia]);

    function isCombinedImage(media: CombinedMedia): media is CombinedImage {
        return media.type === "image";
    }

    const handleEditTag = async (imageId: string, newTag: string) => {
        setSavingTag(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const url = `${backendUrl}/listing/image-tag`;

            const response = await axios.patch(
                url,
                {
                    listingId: propertyData?._id,
                    listingType: propertyData?.listingType,
                    image: { id: imageId, tag: newTag },
                },
                { headers }
            );

            if (response.status === 200) {
                toast.success(
                    <CustomToast
                        message="Tag updated successfully."
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
                // Update local state
                setImages(
                    images.map((img) =>
                        img.id === imageId ? { ...img, tag: newTag } : img
                    )
                );
                if (propertyData && propertyData.images) {
                    const newPropertyData = { ...propertyData };
                    newPropertyData.images = propertyData.images.map((img) =>
                        img.id === imageId ? { ...img, tag: newTag } : img
                    );
                    setPropertyData(newPropertyData);
                }
                setTagEditing(false);
                setTempTag(newTag);
            } else {
                toast.error(
                    <CustomToast
                        message="Failed to update tag."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            }
        } catch (error) {
            console.error("Error updating tag:", error);
            toast.error(
                <CustomToast
                    message="An error occurred while updating the tag."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setSavingTag(false);
        }
    };

    const updateImagesState = (mapFn: (img: Images) => Images) => {
        setImages(images.map(mapFn));
        if (propertyData && propertyData.images) {
            setPropertyData({
                ...propertyData,
                images: propertyData.images.map(mapFn),
            });
        }
    };

    const handleToggleExclude = async (value: boolean) => {
        if (!currentMedia || !isCombinedImage(currentMedia)) return;

        // The primary image is injected into every post and can't be excluded
        // directly — reassign it first via the picker, which excludes it on confirm.
        if (currentMedia.isPrimary && value) {
            setPickerMode("exclude");
            setPickerLockedId(currentMedia.id);
            setPickerOpen(true);
            return;
        }

        const imageId = currentMedia.id;
        setSavingPrimaryOrExclude(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.patch(
                `${backendUrl}/listing/image-exclude`,
                {
                    listingId: propertyData?._id,
                    listingType: propertyData?.listingType,
                    imageId,
                    excludeFromPosts: value,
                },
                { headers }
            );
            if (response.status === 200) {
                updateImagesState((img) =>
                    img.id === imageId
                        ? { ...img, excludeFromPosts: value }
                        : img
                );
                toast.success(
                    <CustomToast
                        message={
                            value
                                ? "Image excluded from posts."
                                : "Image will be used in posts."
                        }
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
            }
        } catch (error) {
            console.error("Error updating exclusion:", error);
            toast.error(
                <CustomToast
                    message={getApiErrorMessage(
                        error,
                        "Failed to update exclusion."
                    )}
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setSavingPrimaryOrExclude(false);
        }
    };

    const handleChangePrimary = () => {
        if (!currentMedia || !isCombinedImage(currentMedia)) return;
        setPickerMode("change");
        setPickerLockedId(currentMedia.id);
        setPickerOpen(true);
    };

    const handlePickerConfirm = async (newPrimaryId: string) => {
        const excludeImageId =
            pickerMode === "exclude" ? pickerLockedId : undefined;
        setSavingPrimaryOrExclude(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.patch(
                `${backendUrl}/listing/primary-image`,
                {
                    listingId: propertyData?._id,
                    listingType: propertyData?.listingType,
                    imageId: newPrimaryId,
                    excludeImageId,
                },
                { headers }
            );
            if (response.status === 200) {
                updateImagesState((img) => {
                    const next: Images = {
                        ...img,
                        isPrimary: img.id === newPrimaryId,
                    };
                    if (excludeImageId && img.id === excludeImageId) {
                        next.excludeFromPosts = true;
                    }
                    return next;
                });
                setPickerOpen(false);
                toast.success(
                    <CustomToast
                        message="Primary image updated."
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
            }
        } catch (error) {
            console.error("Error updating primary image:", error);
            toast.error(
                <CustomToast
                    message={getApiErrorMessage(
                        error,
                        "Failed to update primary image."
                    )}
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setSavingPrimaryOrExclude(false);
        }
    };

    return (
        <div className="relative">
            {currentMedia && isCombinedImage(currentMedia) && (
                <Card className="mt-0">
                    <CardContent className={isMobile ? "p-2 m-0" : "pt-6"}>
                        <div className="flex justify-between items-center">
                            <div className="flex items-center">
                                <IoMdPricetag className="text-secondary w-6 h-6 mr-1" />
                                <CardTitle>
                                    Image Tag{isMobile && ": "}
                                    {isMobile && !tagEditing && (
                                        <span className="ml-1 font-semibold text-primary text-base">
                                            {currentMedia.tag}
                                        </span>
                                    )}
                                </CardTitle>
                            </div>
                            {!tagEditing && (
                                <Button
                                    id="edit-tag-btn"
                                    className={
                                        isMobile ? "ml-2 p-2" : undefined
                                    }
                                    onClick={() => setTagEditing(true)}
                                    disabled={isSyncing}
                                >
                                    <FaEdit className="mr-2 h-6 w-6 text-white" />
                                    {isMobile ? "Edit" : "Edit Tag"}
                                </Button>
                            )}
                        </div>
                        <div
                            className={`mt-4 text-center flex ${
                                isMobile
                                    ? "flex-col items-start"
                                    : "flex-row justify-center items-center"
                            } ${isMobile && !tagEditing ? "hidden" : ""}`}
                        >
                            {tagEditing ? (
                                <InputField
                                    label="Tag"
                                    placeholder="Tag"
                                    value={tempTag}
                                    onChange={(e) => setTempTag(e.target.value)}
                                    type="text"
                                    className={
                                        isMobile
                                            ? "w-full mb-2 text-left"
                                            : "mr-2 text-left"
                                    }
                                />
                            ) : (
                                !isMobile && (
                                    <div className="flex flex-row">
                                        <p className="mr-2 font-semibold text-primary">
                                            Tag:
                                        </p>
                                        <div className="py-2">
                                            {currentMedia.tag ? (
                                                <Badge className="text-sm px-3 py-1">
                                                    {currentMedia.tag}
                                                </Badge>
                                            ) : (
                                                <p className="text-sm text-muted-foreground italic">
                                                    No tag added yet. Add a tag
                                                    to help identify this image.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                        {tagEditing && (
                            <div
                                className={`mt-4 ${
                                    isMobile ? "w-full" : "flex justify-center"
                                }`}
                            >
                                <Button
                                    id="save-tag-btn"
                                    className={isMobile ? "w-full" : undefined}
                                    onClick={() =>
                                        handleEditTag(currentMedia.id, tempTag)
                                    }
                                    disabled={savingTag}
                                >
                                    {savingTag ? "Saving..." : "Save Tag"}
                                </Button>
                            </div>
                        )}

                        <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-4">
                            {currentMedia.isPrimary && (
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-bold text-primary">
                                        <Star className="h-3.5 w-3.5 fill-primary" />
                                        Primary
                                    </span>
                                    <button
                                        type="button"
                                        onClick={handleChangePrimary}
                                        disabled={
                                            isSyncing || savingPrimaryOrExclude
                                        }
                                        className="text-sm font-semibold text-primary underline disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Change primary
                                    </button>
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-3">
                                <div className="flex min-w-0 flex-1 flex-col">
                                    <span className="text-sm font-semibold text-primary">
                                        Exclude from posts
                                    </span>
                                    {currentMedia.excludeFromPosts && (
                                        <span className="text-xs text-[#d50000]">
                                            Excluded - won&apos;t be used in newly
                                            generated posts.
                                        </span>
                                    )}
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    {savingPrimaryOrExclude && (
                                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                    )}
                                    <Switch
                                        checked={!!currentMedia.excludeFromPosts}
                                        onCheckedChange={handleToggleExclude}
                                        disabled={
                                            isSyncing || savingPrimaryOrExclude
                                        }
                                        checkedLabel=""
                                        uncheckedLabel=""
                                        className="data-[state=checked]:bg-[#d50000]"
                                    />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Admin-only image audit card (MB-1314). Fields are returned by the
                API only for admins; rendering is also gated on isAdmin. */}
            {isAdmin && currentMedia && isCombinedImage(currentMedia) && (
                <Card className="mt-2">
                    <CardContent className={isMobile ? "p-2 m-0" : "pt-6"}>
                        <div className="mb-3 flex items-center gap-2">
                            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-primary">
                                Admin only
                            </span>
                            <span className="text-base font-bold text-primary">
                                Image Audit
                            </span>
                        </div>
                        <p className="mb-3 text-xs text-gray-500">
                            AI tagging data — visible to admin users only.
                        </p>

                        <dl className="space-y-3 text-sm">
                            <div>
                                <dt className="font-semibold text-primary">
                                    Description
                                </dt>
                                <dd className="text-gray-700">
                                    {currentMedia.description || "—"}
                                </dd>
                            </div>

                            <div>
                                <dt className="font-semibold text-primary">
                                    Marketing copy
                                </dt>
                                <dd className="text-gray-700">
                                    {currentMedia.marketingCopy || "—"}
                                </dd>
                                {!currentMedia.marketingCopy && (
                                    <dd className="mt-1 text-xs italic text-gray-500">
                                        The marketing brief the slideshow captions
                                        are written from. Populated by the
                                        slideshow re-tag once an image is
                                        used in a slideshow video.
                                    </dd>
                                )}
                            </div>

                            <div>
                                <dt className="font-semibold text-primary">
                                    Recommended for campaign
                                </dt>
                                <dd className="text-gray-700">
                                    {currentMedia.recommendedForCampaign ===
                                    undefined
                                        ? "—"
                                        : currentMedia.recommendedForCampaign
                                          ? "Yes"
                                          : "No"}
                                    {currentMedia.recommendationReason
                                        ? ` — ${currentMedia.recommendationReason}`
                                        : ""}
                                </dd>
                            </div>

                            <div>
                                <dt className="font-semibold text-primary">
                                    Quality score
                                </dt>
                                <dd className="text-gray-700">
                                    {currentMedia.qualityScore != null
                                        ? `${currentMedia.qualityScore} / 5`
                                        : "—"}
                                </dd>
                            </div>

                            <div>
                                <dt className="font-semibold text-primary">
                                    Features
                                </dt>
                                <dd className="text-gray-700">
                                    {currentMedia.imageFeatures &&
                                    currentMedia.imageFeatures.length > 0 ? (
                                        <ul className="ml-4 list-disc">
                                            {currentMedia.imageFeatures.map(
                                                (f, i) => (
                                                    <li key={`${f.name}-${i}`}>
                                                        {f.name}{" "}
                                                        <span className="text-gray-500">
                                                            ({f.position})
                                                        </span>
                                                        {i === 0 && (
                                                            <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary">
                                                                hero
                                                            </span>
                                                        )}
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    ) : (
                                        "—"
                                    )}
                                </dd>
                                {!currentMedia.imageFeatures?.length && (
                                    <dd className="mt-1 text-xs italic text-gray-500">
                                        Feature positions come from the slideshow
                                        re-tag and are populated only
                                        once an image is used in a slideshow
                                        video — they&apos;re not part of the
                                        initial ingestion tagging.
                                    </dd>
                                )}
                            </div>

                            <div>
                                <dt className="font-semibold text-primary">
                                    Tagged by
                                </dt>
                                <dd className="text-gray-700">
                                    {currentMedia.taggedModel || "—"}
                                </dd>
                            </div>
                        </dl>
                    </CardContent>
                </Card>
            )}

            <ChangePrimaryImageDialog
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                images={images}
                mode={pickerMode}
                lockedImageId={pickerLockedId}
                onConfirm={handlePickerConfirm}
                isSaving={savingPrimaryOrExclude}
            />
        </div>
    );
};

export default ImageTagCard;
